import clsx from "clsx";
import { ChangeEvent, FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";

import { AreaPolygonsLegendItem } from "./area-polygons-legend-item";
import { POLYGON_FILTERS } from "./area-polygons-legend.constants";
import {
  AreaPolygonsLegendStyled,
  LegendSwatchStyled,
} from "./area-polygons-legend.styled";
import { IAreaPolygonItem, TPolygonFilter } from "./area-polygons-map.models";
import { hasPolygonFeatures } from "./area-polygons-map.utils";

interface IAreaPolygonsLegendProps {
  areas: IAreaPolygonItem[];
  hiddenIds?: ReadonlySet<number>;
  // The area being reshaped in-map. When set, the legend swaps its header for an
  // editing panel (name + hint + save/cancel) and locks that area's hide toggle.
  editingArea?: IAreaPolygonItem | null;
  isSaving?: boolean;
  onCancelEdit?: () => void;
  onSaveEdit?: () => void;
  onToggle?: (id: number) => void;
  onToggleAllVisible?: (visible: boolean) => void;
  onEditArea?: (id: number) => void;
}

export const AreaPolygonsLegend: FC<IAreaPolygonsLegendProps> = ({
  areas,
  hiddenIds,
  editingArea,
  isSaving,
  onCancelEdit,
  onSaveEdit,
  onToggle,
  onToggleAllVisible,
  onEditArea,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [polygonFilter, setPolygonFilter] = useState<TPolygonFilter>("all");
  const editingId = editingArea?.id ?? null;

  const visibleAreas = useMemo(() => {
    const term = search.trim().toLowerCase();

    return areas.filter(area => {
      const hasPolygon = hasPolygonFeatures(area);

      if (polygonFilter === "with" && !hasPolygon) {
        return false;
      }
      if (polygonFilter === "without" && hasPolygon) {
        return false;
      }

      return (
        !term ||
        area.name.toLowerCase().includes(term) ||
        String(area.id).includes(term)
      );
    });
  }, [areas, search, polygonFilter]);

  const polygonAreas = useMemo(() => areas.filter(hasPolygonFeatures), [areas]);
  const hiddenPolygonCount = polygonAreas.filter(area =>
    hiddenIds?.has(area.id),
  ).length;
  // Checked only when every area is shown; once any is hidden the check is
  // removed and clicking again selects (shows) all — same as the filters.
  const allVisible = polygonAreas.length > 0 && hiddenPolygonCount === 0;

  if (!areas.length) {
    return null;
  }

  return (
    <AreaPolygonsLegendStyled>
      {editingArea && (
        <div className="legend-edit" role="group" aria-live="polite">
          <div className="legend-edit-head">
            <LegendSwatchStyled as="span" $color={editingArea.color} />
            <span className="legend-edit-name">
              {t("area.mapEditShapeTitle", { name: editingArea.name })}
            </span>
          </div>
          <div className="legend-edit-hint">{t("area.mapEditShapeHint")}</div>
          <div className="legend-edit-actions">
            <button
              type="button"
              className="legend-edit-cancel"
              onClick={onCancelEdit}
              disabled={isSaving}
            >
              {t("area.mapEditShapeCancel")}
            </button>
            <button
              type="button"
              className="legend-edit-save"
              onClick={onSaveEdit}
              disabled={isSaving}
            >
              {t("area.mapEditShapeSave")}
            </button>
          </div>
        </div>
      )}

      <div className="legend-title">{t("area.mapLegendTitle")}</div>

      <div className="legend-controls">
        <div className="legend-search-box">
          <SVG_MUI.LensInTextboxSVG className="legend-search-icon" />
          <input
            className="legend-search"
            type="text"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            placeholder={t("area.mapLegendSearchPlaceholder")}
            aria-label={t("area.mapLegendSearchPlaceholder")}
          />
          {!!search && (
            <button
              type="button"
              className="legend-search-clear"
              onClick={() => setSearch("")}
              aria-label={t("area.mapClearSearch")}
              title={t("area.mapClearSearch")}
            >
              <SVG_MUI.XMarkSVG />
            </button>
          )}
        </div>
        <div className="legend-filter" role="group">
          {POLYGON_FILTERS.map(({ value, labelKey }) => (
            <button
              key={value}
              type="button"
              className={clsx(polygonFilter === value && "is-active")}
              aria-pressed={polygonFilter === value}
              onClick={() => setPolygonFilter(value)}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
        {onToggleAllVisible && polygonAreas.length > 0 && (
          <label className="legend-check-all">
            <input
              type="checkbox"
              checked={allVisible}
              onChange={() => onToggleAllVisible(!allVisible)}
            />
            <span>{t("area.mapShowAll")}</span>
          </label>
        )}
      </div>

      <div className="legend-list">
        {visibleAreas.length ? (
          visibleAreas.map(area => (
            <AreaPolygonsLegendItem
              key={area.id}
              area={area}
              isHidden={
                hasPolygonFeatures(area) && Boolean(hiddenIds?.has(area.id))
              }
              isEditing={area.id === editingId}
              onToggle={onToggle}
              onEditArea={onEditArea}
            />
          ))
        ) : (
          <div className="legend-no-results">
            {t("area.mapLegendNoResults")}
          </div>
        )}
      </div>
    </AreaPolygonsLegendStyled>
  );
};
